import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calculator, Type, ArrowLeft, Trophy, Sparkles, Shapes as ShapesIcon, Brain, Medal } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import StickerReward from "../components/StickerReward";
import { Button } from "../components/ui/button";

const WORDS = {
  A: ["Apple", "Ant", "Alligator"], B: ["Ball", "Bear", "Banana"], C: ["Cat", "Car", "Cake"],
  D: ["Dog", "Duck", "Drum"], E: ["Egg", "Elephant", "Eagle"], F: ["Fish", "Frog", "Fox"],
  G: ["Goat", "Grapes", "Gift"], H: ["Hat", "Horse", "House"], L: ["Lion", "Leaf", "Lamp"],
  M: ["Moon", "Mouse", "Milk"], S: ["Sun", "Star", "Snake"], T: ["Tree", "Tiger", "Train"],
};
const LETTERS = Object.keys(WORDS);
const SHAPES = [
  { name: "Circle", emoji: "🔴" }, { name: "Square", emoji: "🟦" }, { name: "Triangle", emoji: "🔺" },
  { name: "Star", emoji: "⭐" }, { name: "Heart", emoji: "💜" }, { name: "Diamond", emoji: "🔷" },
];
const MEMORY_EMOJIS = ["🦁", "🐸", "🦄", "🚀", "🌈", "🐼"];
const ROUNDS = 5;
const MCQ_GAMES = ["math", "letters", "shapes"];

function shuffle(a) { return [...a].sort(() => Math.random() - 0.5); }

function makeMathQuestion(age) {
  let a, b, op;
  if (age <= 5) { a = 1 + Math.floor(Math.random() * 5); b = 1 + Math.floor(Math.random() * 5); op = "+"; }
  else if (age <= 8) { op = Math.random() > 0.5 ? "+" : "-"; a = 2 + Math.floor(Math.random() * 10); b = 1 + Math.floor(Math.random() * a); }
  else { op = Math.random() > 0.5 ? "×" : "+"; a = 2 + Math.floor(Math.random() * 11); b = 2 + Math.floor(Math.random() * 11); }
  const ans = op === "+" ? a + b : op === "-" ? a - b : a * b;
  const opts = new Set([ans]);
  while (opts.size < 4) opts.add(Math.max(0, ans + (Math.floor(Math.random() * 9) - 4)));
  return { display: `${a} ${op} ${b} = ?`, big: false, ans, options: shuffle([...opts]) };
}
function makeLetterQuestion() {
  const letter = LETTERS[Math.floor(Math.random() * LETTERS.length)];
  const correct = WORDS[letter][Math.floor(Math.random() * WORDS[letter].length)];
  const wrongs = shuffle(LETTERS.filter((l) => l !== letter)).slice(0, 3).map((l) => WORDS[l][0]);
  return { display: letter, big: true, ans: correct, options: shuffle([correct, ...wrongs]) };
}
function makeShapeQuestion() {
  const shape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
  const wrongs = shuffle(SHAPES.filter((s) => s.name !== shape.name)).slice(0, 3).map((s) => s.name);
  return { display: shape.emoji, big: true, ans: shape.name, options: shuffle([shape.name, ...wrongs]) };
}
const PROMPTS = { math: "What is…", letters: "Which word starts with…", shapes: "What shape is this?" };

export default function Games() {
  const { activeKid, refreshKid } = useAuth();
  const [mode, setMode] = useState("menu"); // menu | play | memory | result
  const [game, setGame] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [picked, setPicked] = useState(null);
  const [reward, setReward] = useState(null);
  const [kid, setKid] = useState(activeKid);
  // memory
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [moves, setMoves] = useState(0);
  const [lockBoard, setLockBoard] = useState(false);
  const [board, setBoard] = useState([]);

  const loadBoard = () => api.get("/leaderboard").then(({ data }) => setBoard(data));
  useEffect(() => { loadBoard(); }, []);

  useEffect(() => {
    if (activeKid?.profile_id) api.get(`/profiles/${activeKid.profile_id}`).then(({ data }) => setKid(data));
  }, [activeKid?.profile_id]);

  const submitResult = async (g, finalScore, won) => {
    setMode("result");
    setGame(g); setScore(finalScore);
    try {
      const { data } = await api.post(`/profiles/${activeKid.profile_id}/game`, { game: g, score: finalScore, won });
      if (data.sticker) setReward(data.sticker);
      const fresh = await refreshKid();
      if (fresh) setKid(fresh);
      loadBoard();
    } catch (e) { console.error("save game score failed", e); }
  };

  const start = (g) => {
    setGame(g); setIdx(0); setScore(0); setPicked(null); setReward(null);
    if (g === "memory") {
      const deck = shuffle([...MEMORY_EMOJIS, ...MEMORY_EMOJIS].map((e, i) => ({ key: i, emoji: e, matched: false })));
      setCards(deck); setFlipped([]); setMoves(0); setLockBoard(false); setMode("memory");
      return;
    }
    const maker = g === "math" ? () => makeMathQuestion(activeKid.age || 6) : g === "letters" ? makeLetterQuestion : makeShapeQuestion;
    setQuestions(Array.from({ length: ROUNDS }, maker));
    setMode("play");
  };

  const answer = (opt) => {
    if (picked !== null) return;
    setPicked(opt);
    const correct = opt === questions[idx].ans;
    const newScore = score + (correct ? 1 : 0);
    if (correct) setScore(newScore);
    setTimeout(() => {
      if (idx + 1 < ROUNDS) { setIdx(idx + 1); setPicked(null); }
      else submitResult(game, newScore, newScore >= 4);
    }, 650);
  };

  const flipCard = (i) => {
    if (lockBoard || cards[i].matched || flipped.includes(i)) return;
    const next = [...flipped, i];
    setFlipped(next);
    if (next.length === 2) {
      setMoves((m) => m + 1);
      setLockBoard(true);
      const [a, b] = next;
      if (cards[a].emoji === cards[b].emoji) {
        setTimeout(() => {
          const nc = cards.map((c, j) => (j === a || j === b ? { ...c, matched: true } : c));
          setCards(nc); setFlipped([]); setLockBoard(false);
          if (nc.every((c) => c.matched)) {
            const pts = Math.max(1, 12 - moves);
            submitResult("memory", pts, true);
          }
        }, 500);
      } else {
        setTimeout(() => { setFlipped([]); setLockBoard(false); }, 800);
      }
    }
  };

  const stickers = kid?.stickers || [];
  const GAME_META = [
    { id: "math", title: "Number Ninjas", desc: "Solve fun math puzzles", icon: Calculator, color: "#FF5A79", bg: "#FF5A7915", outOf5: true },
    { id: "letters", title: "Letter Heroes", desc: "Match letters to words", icon: Type, color: "#2AB7CA", bg: "#2AB7CA15", outOf5: true },
    { id: "shapes", title: "Shape Safari", desc: "Spot the right shape", icon: ShapesIcon, color: "#FFC02D", bg: "#FFC02D15", outOf5: true },
    { id: "memory", title: "Memory Match", desc: "Find matching pairs", icon: Brain, color: "#9B5DE5", bg: "#9B5DE515", outOf5: false },
  ];

  return (
    <div className="min-h-screen bg-[#FFFDF7] pb-16">
      <Navbar />
      <StickerReward sticker={reward} onClose={() => setReward(null)} />
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-1">
          <span className="h-10 w-10 rounded-2xl bg-[#9B5DE5] text-white flex items-center justify-center text-xl">🎮</span>
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-slate-900">Learning Games</h1>
        </div>
        <p className="text-slate-500 mb-8">Play, score points and collect stickers{activeKid?.name ? `, ${activeKid.name}` : ""}! 🌟</p>

        <AnimatePresence mode="wait">
          {mode === "menu" && (
            <motion.div key="menu" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="grid sm:grid-cols-2 gap-6" data-testid="games-menu">
              {GAME_META.map((g) => (
                <motion.button key={g.id} data-testid={`game-${g.id}`} whileHover={{ y: -6, scale: 1.02 }} onClick={() => start(g.id)}
                  className="text-left rounded-3xl p-8 border border-amber-100/60 shadow-lg bg-white hover:shadow-xl transition-all">
                  <span className="h-16 w-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: g.bg, color: g.color }}>
                    <g.icon className="h-8 w-8" />
                  </span>
                  <h2 className="mt-4 text-2xl font-display font-bold text-slate-900">{g.title}</h2>
                  <p className="text-slate-500 mt-1">{g.desc}</p>
                  {kid?.game_scores?.[g.id] != null && (
                    <div className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-amber-600">
                      <Trophy className="h-4 w-4" /> Best: {kid.game_scores[g.id]}{g.outOf5 ? "/5" : " pts"}
                    </div>
                  )}
                </motion.button>
              ))}
            </motion.div>
          )}

          {mode === "play" && (
            <motion.div key="play" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
              className="bg-white rounded-3xl border border-amber-100/60 shadow-lg p-8" data-testid="game-play">
              <div className="flex items-center justify-between mb-6">
                <span className="text-sm font-bold text-slate-400">Question {idx + 1} / {ROUNDS}</span>
                <span className="rounded-full bg-emerald-100 text-emerald-700 px-3 py-1 text-sm font-bold">Score {score}</span>
              </div>
              <div className="text-center py-6">
                <div className="text-xs uppercase tracking-widest font-bold text-[#9B5DE5]">{PROMPTS[game]}</div>
                <div className={`font-display font-bold text-slate-900 my-4 ${questions[idx].big ? "text-8xl" : "text-6xl"}`} data-testid="question-text">
                  {questions[idx].display}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {questions[idx].options.map((opt, i) => {
                  const isCorrect = opt === questions[idx].ans;
                  const show = picked !== null;
                  const cls = show
                    ? isCorrect ? "bg-emerald-500 text-white border-emerald-500"
                      : opt === picked ? "bg-rose-400 text-white border-rose-400" : "bg-white text-slate-400 border-slate-200"
                    : "bg-white text-slate-800 border-slate-200 hover:border-[#9B5DE5] hover:bg-purple-50";
                  return (
                    <motion.button key={i} data-testid={`answer-${i}`} whileTap={{ scale: 0.96 }} disabled={show}
                      onClick={() => answer(opt)} className={`rounded-2xl border-2 py-6 text-2xl font-bold transition-all ${cls}`}>
                      {opt}
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {mode === "memory" && (
            <motion.div key="memory" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
              className="bg-white rounded-3xl border border-amber-100/60 shadow-lg p-8" data-testid="game-memory">
              <div className="flex items-center justify-between mb-6">
                <span className="text-sm font-bold text-slate-400">Find all the pairs!</span>
                <span className="rounded-full bg-purple-100 text-purple-700 px-3 py-1 text-sm font-bold">Moves {moves}</span>
              </div>
              <div className="grid grid-cols-4 gap-3 sm:gap-4 max-w-md mx-auto">
                {cards.map((c, i) => {
                  const faceUp = c.matched || flipped.includes(i);
                  return (
                    <motion.button key={c.key} data-testid={`memory-card-${i}`} whileTap={{ scale: 0.95 }} onClick={() => flipCard(i)}
                      className={`aspect-square rounded-2xl text-3xl sm:text-4xl flex items-center justify-center font-bold transition-all border-2 ${
                        faceUp ? (c.matched ? "bg-emerald-50 border-emerald-300" : "bg-purple-50 border-purple-300") : "bg-[#9B5DE5] border-[#9B5DE5] text-white"
                      }`}>
                      {faceUp ? c.emoji : "?"}
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {mode === "result" && (
            <motion.div key="result" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-3xl border border-amber-100/60 shadow-lg p-10 text-center" data-testid="game-result">
              <div className="text-6xl mb-3">🏆</div>
              <h2 className="text-3xl font-display font-bold text-slate-900">
                {game === "memory" ? "All matched!" : score >= 4 ? "You won!" : "Good try!"}
              </h2>
              <p className="text-slate-500 mt-2">
                {game === "memory"
                  ? <>You finished in <span className="font-bold text-slate-800">{moves} moves</span> and earned a sticker!</>
                  : <>You scored <span className="font-bold text-slate-800">{score} / {ROUNDS}</span>{score >= 4 ? " and earned a sticker!" : ". Get 4+ to win a sticker."}</>}
              </p>
              <div className="flex gap-3 justify-center mt-6">
                <Button data-testid="play-again-btn" onClick={() => start(game)} className="rounded-full bg-[#9B5DE5] hover:bg-[#8a4fd0] font-bold px-6 h-12">Play again</Button>
                <Button variant="outline" onClick={() => setMode("menu")} className="rounded-full font-bold px-6 h-12 border-2">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Games menu
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-12">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="h-5 w-5 text-[#FFC02D]" />
            <h2 className="text-xl font-display font-bold text-slate-900">My Sticker Shelf</h2>
            <span className="text-sm text-slate-400">({stickers.length} collected)</span>
          </div>
          <div className="flex flex-wrap gap-4" data-testid="sticker-shelf">
            {stickers.map((s, i) => (
              <motion.div key={s.id + i} whileHover={{ scale: 1.1, rotate: 5 }} title={`${s.name} — ${s.reason}`}
                className="h-20 w-20 rounded-2xl bg-white border border-amber-100 shadow flex flex-col items-center justify-center">
                <span className="text-3xl">{s.emoji}</span>
                <span className="text-[10px] font-bold text-slate-500 mt-0.5 text-center leading-tight">{s.name}</span>
              </motion.div>
            ))}
            {Array.from({ length: Math.max(0, 6 - stickers.length) }).map((_, i) => (
              <div key={`empty-${i}`} className="h-20 w-20 rounded-2xl border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-300 text-2xl">?</div>
            ))}
          </div>
        </div>

        {/* Family leaderboard */}
        <div className="mt-12">
          <div className="flex items-center gap-2 mb-4">
            <Medal className="h-5 w-5 text-[#FF5A79]" />
            <h2 className="text-xl font-display font-bold text-slate-900">Family Leaderboard</h2>
          </div>
          <div className="space-y-3" data-testid="leaderboard">
            {board.map((r, i) => {
              const medals = ["🥇", "🥈", "🥉"];
              const isMe = activeKid?.profile_id === r.profile_id;
              return (
                <motion.div key={r.profile_id} data-testid={`leaderboard-row-${i}`} whileHover={{ x: 3 }}
                  className={`flex items-center gap-4 rounded-2xl p-4 border ${isMe ? "border-[#FF5A79] bg-rose-50" : "border-amber-100/60 bg-white"} shadow`}>
                  <span className="text-2xl w-8 text-center">{medals[i] || `#${i + 1}`}</span>
                  <span className="h-11 w-11 rounded-xl flex items-center justify-center text-2xl" style={{ backgroundColor: r.avatar_color + "22" }}>{r.avatar_emoji}</span>
                  <div className="flex-1">
                    <div className="font-bold text-slate-800">{r.name}{isMe && <span className="text-xs text-[#FF5A79] ml-2">You</span>}</div>
                    <div className="text-xs text-slate-400">⭐ {r.stickers} stickers</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-display font-bold text-[#FF5A79]">{r.points}</div>
                    <div className="text-xs text-slate-400">points</div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
